import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { getOperationAST } from 'graphql/utilities';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { IRefreshGridEventBusMessage, REFRESH_GRID } from '@pages-clean/risk-assessment/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { busDispatch } from '@utils-clean/eventBus';
import {
    updateControlsHubChartConfig,
    updateControlsHubChartConfigVariables,
} from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROLS_HUB_CHART_CONFIG, UPSERT_CM_CONTROLS_HUB_CHART_CONFIG } from '@queries/risk-assessment';

interface UnassignControlProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
}

const CmControlsNewDatasetInfoDialog = ({ onClose, onSubmit }: UnassignControlProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.cmControlsNewDatasetData' });
    const { current } = useContext(RiskLogContext);
    const { study } = current;
    const [upsertControlsHubChartConfig] = useMutation<
        updateControlsHubChartConfig,
        updateControlsHubChartConfigVariables
    >(UPSERT_CM_CONTROLS_HUB_CHART_CONFIG);

    const handleSubmit = async () => {
        const operationName = getOperationAST(GET_CM_CONTROLS_HUB_CHART_CONFIG)?.name?.value;
        await upsertControlsHubChartConfig({
            variables: {
                studyId: study?.id,
                cutoffDate: assumeUtc(current.latestDataset!),
            },
            refetchQueries: [operationName!],
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.updateControlsHubChartConfig) {
                    onSubmit('error');
                    return;
                }
                busDispatch<IRefreshGridEventBusMessage>(REFRESH_GRID, true);
                onSubmit('success');
            })
            .catch(() => {
                onSubmit('error');
            });
    };

    return (
        <ConfirmationDialog
            title={t('title')}
            dataTestId="cm-controls-new-dataset-data-confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={() => handleSubmit()}
        >
            <div>
                <InfoField
                    type="Info"
                    message={
                        <div style={{ width: '438px', margin: '0 0' }}>
                            <div>{t('description')}</div>
                        </div>
                    }
                />
                <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '18px' }}>{t('message')}</div>
            </div>
        </ConfirmationDialog>
    );
};

export default CmControlsNewDatasetInfoDialog;
